import React from 'react';
import { X, Zap, ArrowRight, Lock } from 'lucide-react';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';

const PLAN_LABELS = { weekly: 'Weekly', monthly: 'Monthly', quarterly: '3 Months', premium: 'Premium' };

const fmt = (amount) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

const AbandonedPaymentBanner = ({ payment, onDismiss }) => {
  const navigate = useLocalizedNavigate();

  const planLabel = PLAN_LABELS[payment.planId] || payment.planName || 'Premium';
  const amount = typeof payment.amount === 'number' ? fmt(payment.amount) : null;

  const handleContinue = () => {
    // A retired plan (monthly) is mapped to 3 months by the checkout page.
    const params = new URLSearchParams({ planId: payment.planId || 'quarterly' });
    if (payment.planName) params.set('planName', payment.planName);
    if (payment.amount) params.set('amount', payment.amount);
    navigate(`/checkout?${params}`);
  };

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border-[3px] border-black bg-white shadow-[6px_6px_0px_0px_rgba(58,134,255,1)] animate-fade-in-up">
      {/* Blue left accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#3A86FF]" />

      {/* Main row */}
      <div className="pl-5 pr-4 pt-4 pb-4 sm:pb-4 flex items-start sm:items-center gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#3A86FF] flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(0,0,0,0.15)]">
          <Zap className="w-5 h-5 fill-white text-white" />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="font-black text-black text-sm uppercase tracking-tight leading-tight">
            Your {planLabel} plan is waiting
          </p>
          <p className="text-xs text-black/50 font-semibold mt-0.5 leading-snug">
            You started checkout{amount ? ` for ${amount}` : ''}, pick up where you left off.
          </p>
        </div>

        {/* Desktop CTA + dismiss */}
        <div className="hidden sm:flex items-center gap-2 flex-shrink-0 ml-2">
          <button
            onClick={handleContinue}
            className="flex items-center gap-1.5 bg-black text-white text-xs font-black px-4 py-2.5 rounded-xl border-[2px] border-black hover:bg-[#3A86FF] hover:border-[#3A86FF] transition-all duration-200 active:scale-[0.97] min-h-[40px] whitespace-nowrap touch-manipulation"
          >
            Continue <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDismiss(payment.id)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-black/30 hover:text-black hover:bg-black/5 transition-all touch-manipulation"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile dismiss, top-right only */}
        <button
          onClick={() => onDismiss(payment.id)}
          className="sm:hidden flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-black/30 hover:text-black hover:bg-black/5 transition-all touch-manipulation"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Mobile full-width CTA */}
      <div className="sm:hidden px-4 pb-4">
        <button
          onClick={handleContinue}
          className="w-full flex items-center justify-center gap-2 bg-black text-white text-sm font-black py-3.5 rounded-xl border-[2px] border-black hover:bg-[#3A86FF] hover:border-[#3A86FF] transition-all duration-200 active:scale-[0.97] min-h-[48px] touch-manipulation"
        >
          <Lock className="w-4 h-4" />
          Continue to checkout
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default AbandonedPaymentBanner;
